// Settings: the Google mailbox, spa details, sign out. Speed, sender addresses and password sit under Advanced. Rarely visited, so plain and short.

import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { api, errorText } from '../lib/api';
import { navigate, useLocation } from '../lib/router';
import { Button, Field, LoadError, Notice, Spinner, Toggle, confirmBox, copyText, toast, toastError, useLoad } from '../lib/ui';
import { GoogleLogo, Icon } from '../lib/icons';
import { num } from '../lib/format';
import { loadSettings, setSettingsBundle, type SettingsBundle } from '../lib/store';
import type { ProviderSetting, Settings as S } from '../../shared/types';

interface GoogleStatus {
  clientSet: boolean;
  clientFromServer: boolean;
  clientIdEnd: string | null;
  connected: boolean;
  account: string | null;
  error: string | null;
  redirectUri: string;
  origin: string;
  fromMatches: boolean;
  fromEmail: string;
}

const GOOGLE_MSG: Record<string, { kind: 'ok' | 'bad' | 'warn'; text: string }> = {
  connected: { kind: 'ok', text: 'Gmail is connected. Emails will go out from your mailbox.' },
  denied: { kind: 'warn', text: "Google didn't get permission. Try again and press Allow on the last screen." },
  expired: { kind: 'warn', text: 'That took too long. Try again.' },
  'wrong-account': { kind: 'bad', text: 'That Google account is not the one this app sends from. Tap Connect Google and pick the spa mailbox.' },
  error: { kind: 'bad', text: 'Google sent back an error. Wait a minute, then tap Reconnect. If it keeps happening, tap Help.' },
};

export function Settings() {
  const { query } = useLocation();
  const bundle = useLoad(() => loadSettings(true), []);
  const google = useLoad(() => api<GoogleStatus>('/api/google/status'), []);
  const msg = GOOGLE_MSG[query.get('google') || ''];
  const people = useLoad(() => api<{ count: number }>(`/api/contacts/count?audience=${encodeURIComponent(JSON.stringify({ kind: 'all' }))}`).then((r) => r.count), []).data;

  if (bundle.error) return <div class="wrap"><LoadError error={bundle.error} retry={bundle.reload} /></div>;
  if (!bundle.data) return <Spinner />;

  return (
    <div class="wrap narrow-l settings">
      <div class="page-head">
        <div>
          <h1 class="display">Settings</h1>
        </div>
      </div>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      <GoogleCard g={google.data} error={google.error} reload={google.reload} summary={<SpeedSummary b={bundle.data} people={people} />} />
      <SpaCard b={bundle.data} />
      <SignOutCard />
      <Advanced b={bundle.data} g={google.data} reloadGoogle={google.reload} people={people} />
    </div>
  );
}

// ---------- Google ----------

function GoogleCard({ g, error, reload, summary }: { g: GoogleStatus | null; error: string | null; reload: () => void; summary: ComponentChildren }) {
  const [busy, setBusy] = useState(false);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!g) return <section class="card"><Spinner /></section>;
  const disconnect = async () => {
    const ok = await confirmBox({ title: 'Disconnect Gmail?', body: 'Nothing will send until you connect again. Scheduled emails wait.', ok: 'Disconnect', danger: true });
    if (!ok) return;
    setBusy(true);
    try {
      await api('/api/google/disconnect', { method: 'POST' });
      toast('Disconnected.');
      reload();
      loadSettings(true);
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section class="card">
      <h2 class="card-title">Sending mailbox</h2>
      {g.connected ? (
        <div class="google-row">
          <span class="google-badge">
            <GoogleLogo size={20} />
          </span>
          <div class="grow">
            <b>{g.account}</b>
            <small class="ok-text">
              <Icon name="check" size={14} /> Connected. Emails go out from this address.
            </small>
          </div>
          <Button size="s" kind="quiet" onClick={() => (location.href = '/api/google/start?mode=connect')}>
            Reconnect
          </Button>
          <Button size="s" kind="quiet" class="danger-text" busy={busy} onClick={disconnect}>
            Disconnect
          </Button>
        </div>
      ) : (
        <div class="google-row">
          <span class="google-badge">
            <GoogleLogo size={20} />
          </span>
          <div class="grow">
            <b>Not connected</b>
          </div>
          <a class="google-btn" href="/api/google/start?mode=connect" aria-disabled={!g.clientSet}>
            <GoogleLogo size={18} />
            Connect Google
          </a>
        </div>
      )}
      {g.error && <Notice kind="bad">{g.error}</Notice>}
      {g.connected && !g.fromMatches && (
        <Notice kind="warn">
          Emails say they're from {g.fromEmail}, but the connected mailbox is {g.account}. Gmail will rewrite the sender. Change "Send from" under Advanced, or reconnect with {g.fromEmail}.
        </Notice>
      )}
      {!g.clientSet && <Notice kind="info">Google sign-in isn't set up on the server yet. Tap Help if you see this.</Notice>}
      {summary}
    </section>
  );
}

// ---------- spa details ----------

type SpaKeys = 'from_name' | 'address' | 'phone' | 'website' | 'booking_url' | 'instagram' | 'support_email';

function SpaCard({ b }: { b: SettingsBundle }) {
  const pick = (s: S) => ({
    from_name: s.from_name,
    address: s.address,
    phone: s.phone,
    website: s.website,
    booking_url: s.booking_url,
    instagram: s.instagram,
    support_email: s.support_email,
    test_emails: s.test_emails.join(', '),
  });
  const [f, setF] = useState(pick(b.settings));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setF(pick(b.settings)), [b.settings]);
  const orig = pick(b.settings);
  const dirty = JSON.stringify(f) !== JSON.stringify(orig);
  const inp = (k: SpaKeys | 'test_emails', type = 'text') => <input type={type} value={f[k]} onInput={(e) => setF({ ...f, [k]: e.currentTarget.value })} />;

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { test_emails, ...rest } = f;
      const r = await api<SettingsBundle>('/api/settings', {
        method: 'PUT',
        body: { ...rest, test_emails: test_emails.split(/[\s,;]+/).filter(Boolean) },
      });
      setSettingsBundle(r);
      toast('Saved.');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section class="card">
      <h2 class="card-title spa-title">
        <img class="spa-logo" src="/brand/logo.png" alt="" />
        <span>Your spa</span>
      </h2>
      <div class="form-grid">
        <div class="two">
          <Field label="Name people see">{inp('from_name')}</Field>
          <Field label="Phone">{inp('phone', 'tel')}</Field>
        </div>
        <Field label="Address" hint="Required by law.">
          {inp('address')}
        </Field>
        <Field label="Website">{inp('website', 'url')}</Field>
        <Field label="Booking link">{inp('booking_url', 'url')}</Field>
        <Field label="Instagram">{inp('instagram', 'url')}</Field>
        <Field label="Test emails go to" hint="You get a test copy here before every send. Up to 5, with commas.">
          <input type="text" value={f.test_emails} placeholder={b.settings.from_email} onInput={(e) => setF({ ...f, test_emails: e.currentTarget.value })} />
        </Field>
      </div>
      {err && <Notice kind="bad">{err}</Notice>}
      <div class="card-foot">
        <Button kind="primary" busy={busy} disabled={!dirty} onClick={save}>
          Save
        </Button>
        {dirty && (
          <Button kind="quiet" onClick={() => setF(orig)}>
            Undo changes
          </Button>
        )}
      </div>
    </section>
  );
}

// ---------- sending speed ----------

function perDayOf(perMinute: number, daily: number, start: number, end: number): number {
  return Math.min(daily, perMinute * 60 * Math.max(1, end - start));
}

/** The one line Jen needs: how long an email to everyone takes. The controls live under Advanced. */
function SpeedSummary({ b, people }: { b: SettingsBundle; people: number | null }) {
  const gmail = b.settings.providers.find((p) => p.name === 'gmail');
  if (!gmail || !people) return null;
  const perDay = perDayOf(gmail.perMinute, gmail.dailyLimit, b.settings.send_start_hour, b.settings.send_end_hour);
  return (
    <p class="speed-sum">
      <Icon name="clock" size={16} /> Sending to all {num(people)} takes {howLong(people, gmail.perMinute * 60, perDay)}.
    </p>
  );
}

function SpeedControls({ b, people }: { b: SettingsBundle; people: number | null }) {
  const gmail = b.settings.providers.find((p) => p.name === 'gmail')!;
  const status = b.providers.find((p) => p.name === 'gmail');
  const [perMinute, setPerMinute] = useState(gmail.perMinute);
  const [daily, setDaily] = useState(gmail.dailyLimit);
  const [start, setStart] = useState(b.settings.send_start_hour);
  const [end, setEnd] = useState(b.settings.send_end_hour);
  const [busy, setBusy] = useState(false);
  const dirty = perMinute !== gmail.perMinute || daily !== gmail.dailyLimit || start !== b.settings.send_start_hour || end !== b.settings.send_end_hour;
  const perHour = perMinute * 60;
  const perDay = perDayOf(perMinute, daily, start, end);

  const save = async () => {
    setBusy(true);
    try {
      const providers: ProviderSetting[] = b.settings.providers.map((p) => (p.name === 'gmail' ? { ...p, perMinute, dailyLimit: daily } : p));
      const r = await api<SettingsBundle>('/api/settings', { method: 'PUT', body: { providers, send_start_hour: start, send_end_hour: end } });
      setSettingsBundle(r);
      toast('Saved.');
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="adv-speed">
      <b>Sending speed</b>
      <div class="speed">
        <Field label="Emails per minute" right={<b>{perMinute}</b>}>
          <input type="range" min={1} max={10} value={perMinute} onInput={(e) => setPerMinute(Number(e.currentTarget.value))} />
        </Field>
        <Field label="Most per day" right={<b>{num(daily)}</b>}>
          <input type="range" min={100} max={1500} step={50} value={daily} onInput={(e) => setDaily(Number(e.currentTarget.value))} />
        </Field>
        <div class="two">
          <Field label="Start sending at">
            <select class="select" value={start} onChange={(e) => setStart(Number(e.currentTarget.value))}>
              {hourOptions(0, 23)}
            </select>
          </Field>
          <Field label="Stop at">
            <select class="select" value={end} onChange={(e) => setEnd(Number(e.currentTarget.value))}>
              {hourOptions(1, 24)}
            </select>
          </Field>
        </div>
      </div>
      <p class="speed-sum">
        <Icon name="clock" size={16} /> About <b>{num(perDay)}</b> a day, Central time.{people != null && people > 0 && <> All {num(people)} take <b>{howLong(people, perHour, perDay)}</b>.</>}
        {status && <span class="muted"> {num(status.sentToday)} sent today.</span>}
      </p>
      {end <= start && <Notice kind="warn">The stop time has to be after the start time.</Notice>}
      <div class="card-foot">
        <Button kind="primary" busy={busy} disabled={!dirty || end <= start} onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );
}

function hourOptions(from: number, to: number) {
  const out = [];
  for (let h = from; h <= to; h++) {
    const label = h === 0 || h === 24 ? 'midnight' : h === 12 ? 'noon' : h < 12 ? `${h} am` : `${h - 12} pm`;
    out.push(
      <option key={h} value={h}>
        {label}
      </option>,
    );
  }
  return out;
}

function howLong(n: number, perHour: number, perDay: number): string {
  if (perDay <= 0) return 'a long time';
  if (n <= perDay) {
    const h = Math.max(1, Math.round(n / perHour));
    return h === 1 ? 'about an hour' : `about ${h} hours`;
  }
  return `${Math.ceil(n / perDay)} days`;
}

// ---------- sender addresses, password, sign out ----------

/** Send from and Replies go to. Both are the spa mailbox, so they sit under Advanced. */
function SenderFields({ b }: { b: SettingsBundle }) {
  const pick = (s: S) => ({ from_email: s.from_email, reply_to: s.reply_to });
  const [f, setF] = useState(pick(b.settings));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => setF(pick(b.settings)), [b.settings]);
  const dirty = f.from_email !== b.settings.from_email || f.reply_to !== b.settings.reply_to;
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      setSettingsBundle(await api<SettingsBundle>('/api/settings', { method: 'PUT', body: f }));
      toast('Saved.');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="adv-block">
      <b>Sender addresses</b>
      <div class="two">
        <Field label="Send from">
          <input type="email" value={f.from_email} onInput={(e) => setF({ ...f, from_email: e.currentTarget.value })} />
        </Field>
        <Field label="Replies go to">
          <input type="email" value={f.reply_to} onInput={(e) => setF({ ...f, reply_to: e.currentTarget.value })} />
        </Field>
      </div>
      {err && <Notice kind="bad">{err}</Notice>}
      <Button size="s" busy={busy} disabled={!dirty} onClick={save}>
        Save
      </Button>
    </div>
  );
}

/** The backup way to sign in. Jen uses Google, so this sits under Advanced. */
function PasswordBlock() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const change = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api('/api/password', { body: { current: cur, next } });
      toast('Password changed. Sign in again with the new one.');
      navigate('/login', { replace: true });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="adv-block">
      <b>Backup password</b>
      {open ? (
        <>
          <div class="two">
            <Field label="Current password">
              <input type="password" autoComplete="current-password" value={cur} onInput={(e) => setCur(e.currentTarget.value)} />
            </Field>
            <Field label="New password" hint="12 or more characters. A few random words work well.">
              <input type="password" autoComplete="new-password" value={next} onInput={(e) => setNext(e.currentTarget.value)} />
            </Field>
          </div>
          {err && <Notice kind="bad">{err}</Notice>}
          <div class="card-foot">
            <Button size="s" kind="primary" busy={busy} disabled={!cur || next.length < 12} onClick={change}>
              Change password
            </Button>
            <Button size="s" kind="quiet" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <Button size="s" icon="lock" onClick={() => setOpen(true)}>
          Change password
        </Button>
      )}
    </div>
  );
}

function SignOutCard() {
  const logout = async () => {
    try {
      await api('/api/logout', { method: 'POST' });
    } catch {
      /* signed out either way */
    }
    navigate('/login', { replace: true });
  };
  return (
    <section class="card signout-card">
      <h2 class="card-title">Signing in</h2>
      <Button icon="logout" onClick={logout}>
        Sign out
      </Button>
    </section>
  );
}

// ---------- advanced ----------

function Advanced({ b, g, reloadGoogle, people }: { b: SettingsBundle; g: GoogleStatus | null; reloadGoogle: () => void; people: number | null }) {
  const brevo = b.settings.providers.find((p) => p.name === 'brevo');
  const brevoStatus = b.providers.find((p) => p.name === 'brevo');
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState('');
  const [secret, setSecret] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const toggleBrevo = async (on: boolean) => {
    setBusy(true);
    try {
      const providers = b.settings.providers.map((p) => (p.name === 'brevo' ? { ...p, enabled: on } : p));
      setSettingsBundle(await api<SettingsBundle>('/api/settings', { method: 'PUT', body: { providers } }));
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };

  const saveKeys = async () => {
    setErr(null);
    try {
      await api('/api/google/client', { method: 'PUT', body: { client_id: id, client_secret: secret } });
      toast('Saved. Now press Connect Google.');
      setId('');
      setSecret('');
      reloadGoogle();
    } catch (e) {
      setErr(errorText(e));
    }
  };

  return (
    <details class="card advanced">
      <summary>
        <span>Advanced</span>
        <Icon name="down" size={18} />
      </summary>
      <div class="adv-body">
        <SpeedControls b={b} people={people} />
        <SenderFields b={b} />
        <PasswordBlock />
        {brevo && (
          <div class="adv-row">
            <div class="grow">
              <b>Backup sender (Brevo)</b>
              <small class="muted">{brevoStatus?.detail || 'Used only if Gmail stops working.'}</small>
            </div>
            <Toggle on={brevo.enabled} label="Use the backup sender" onChange={(v) => !busy && toggleBrevo(v)} />
          </div>
        )}
        {g && !g.clientFromServer && (
          <div class="adv-keys">
            <b>Google app keys</b>
            <small class="muted">
              Only needed if support asks. {g.clientSet ? `Keys saved (${g.clientIdEnd}).` : 'No keys saved.'} Redirect address:{' '}
              <button class="link" onClick={() => copyText(g.redirectUri, 'Redirect address copied')}>
                {g.redirectUri}
              </button>
            </small>
            <div class="two">
              <Field label="Client ID">
                <input value={id} onInput={(e) => setId(e.currentTarget.value)} autoComplete="off" />
              </Field>
              <Field label="Client secret">
                <input type="password" value={secret} onInput={(e) => setSecret(e.currentTarget.value)} autoComplete="off" />
              </Field>
            </div>
            {err && <Notice kind="bad">{err}</Notice>}
            <Button size="s" disabled={!id || !secret} onClick={saveKeys}>
              Save keys
            </Button>
          </div>
        )}
        <p class="muted small">App: {b.appEnv}</p>
      </div>
    </details>
  );
}
